/**
 * Agent transcript types + lookup helpers for the spectator overlay.
 *
 * Mirrors the v1.0.0 schema in `docs/dev/launch-wiring/02-transcript-
 * transport.md §4`. One entry per LLM call across a match; the overlay
 * cares mostly about `phase: "decide"` entries since those carry the
 * per-turn reasoning and tool calls.
 *
 * The overlay package owns the structural type so it can render
 * reasoning without taking a runtime dep on `api-types` (the consumer
 * passes pre-parsed entries; the OBS overlay variant fetches via the
 * Researcher API; the desktop client reads local bench JSONL).
 *
 * Fields beyond the canonical schema are typed as `unknown` so future
 * server additions don't break the renderer.
 */

export type TranscriptPhase =
  | "brief"
  | "draft"
  | "deploy"
  | "decide"
  | "sitrep"
  | "surrender"
  | "debrief";

export interface TranscriptLlmMeta {
  base_url?: string;
  model?: string;
  system_prompt_hash?: string;
  tool_messages_count?: number;
}

export interface TranscriptRequest {
  messages_count?: number;
  tokens_in_estimated?: number;
  temperature?: number;
  max_tokens?: number;
  tools_offered?: string[];
}

export interface TranscriptToolCall {
  name: string;
  arguments: Record<string, unknown>;
}

export interface TranscriptResponse {
  tokens_out?: number;
  latency_ms?: number;
  finish_reason?: string;
  /** Present only when the LLM emitted explicit reasoning. */
  reasoning_text?: string;
  tool_calls?: TranscriptToolCall[];
}

export interface TranscriptOutcome {
  actions_emitted?: number;
  /** Index range into the engine replay event stream. The viewer uses
   *  this to cross-reference "what the LLM thought" with "what the
   *  engine did" — see transcript-transport.md:164. */
  engine_event_index_range?: [number, number];
}

export interface TranscriptEntry {
  schema_version?: string;
  match_id?: string;
  agent_id?: string;
  engineer_id?: string;
  /** 1-indexed combat turn; 0 for Brief/Draft/Deploy. */
  turn: number;
  phase: TranscriptPhase;
  issued_at?: string;
  llm?: TranscriptLlmMeta;
  request?: TranscriptRequest;
  response?: TranscriptResponse;
  outcome?: TranscriptOutcome;
}

/**
 * Find the entry that explains the actions resolved at `turn`. The
 * engine resolves actions at turn N from the Decide RPC that fired
 * at the start of turn N, so the lookup is exact-match on `turn`
 * + `phase: "decide"`.
 *
 * Returns `null` when no matching entry exists (early in a match
 * before the agent has Decided, or for replays where the consumer
 * passed an empty / unloaded transcript).
 */
export function decidePhaseAt(
  transcript: ReadonlyArray<TranscriptEntry>,
  turn: number,
): TranscriptEntry | null {
  for (const e of transcript) {
    if (e.phase === "decide" && e.turn === turn) return e;
  }
  return null;
}

/**
 * The most recent Decide-phase entry at or before `turn`. Useful for
 * scrubbing to a tick where the agent has been silent (no new Decide
 * fired this turn — agent ran out of time or skipped) — the panel
 * shows the last reasoning instead of going blank.
 */
export function lastDecidePhaseAtOrBefore(
  transcript: ReadonlyArray<TranscriptEntry>,
  turn: number,
): TranscriptEntry | null {
  let best: TranscriptEntry | null = null;
  for (const e of transcript) {
    if (e.phase !== "decide") continue;
    if (e.turn > turn) continue;
    if (best === null || e.turn > best.turn) best = e;
  }
  return best;
}

/**
 * Pretty-print one tool call as a compact one-liner.
 *   { name: "fire_tool", arguments: { weapon_idx: 2, target_zone: "FRONT_CT" } }
 *   → "fire_tool(weapon_idx=2, target_zone=FRONT_CT)"
 *
 * Keeps the rendering Reasoning Panel cheap (no nested JSON) while
 * still surfacing what the LLM decided.
 */
export function formatToolCall(tc: TranscriptToolCall): string {
  const args = Object.entries(tc.arguments)
    .map(([k, v]) => `${k}=${formatArgValue(v)}`)
    .join(", ");
  return `${tc.name}(${args})`;
}

function formatArgValue(v: unknown): string {
  if (v === null) return "null";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return `[${v.map(formatArgValue).join(",")}]`;
  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return "{…}";
    }
  }
  return String(v);
}
